import {
  Badge,
  Box,
  Button,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { useEffect, useState } from "react";
import {
  ColumnFilterConfig,
  ColumnFilterType,
  DateRangeValue,
} from "./columnFilterTypes";
import { ColumnSortConfig, getSortOptionLabels, SortDirection } from "./columnSortTypes";
import { isColumnSorted } from "../../../utils/columnSortActive";
import YesNoFilterField from "./participantFilterFields/YesNoFilterField";
import MultiSelectFilterField from "./participantFilterFields/MultiSelectFilterField";
import SingleSelectFilterField from "./participantFilterFields/SingleSelectFilterField";
import TextFilterField from "./participantFilterFields/TextFilterField";
import DateRangeFilterField from "./participantFilterFields/DateRangeFilterField";
import TagsAutocompleteFilterField from "./participantFilterFields/TagsAutocompleteFilterField";
import CustomColumnFilterField, {
  CustomColumnDef,
} from "./participantFilterFields/CustomColumnFilterField";
import {
  decodeNumberFilter,
  getBetweenError,
} from "./participantFilterFields/customColumnFilterUtils";

export type ColumnFilterValue =
  | string
  | string[]
  | DateRangeValue
  | { key: string; value: string | string[] };

type Props = {
  label: string;
  filterActive?: boolean;
  filterConfig?: ColumnFilterConfig;
  filterValue?: ColumnFilterValue;
  onFilterApply?: (value: ColumnFilterValue) => void;
  onFilterClear?: () => void;
  sortConfig?: ColumnSortConfig;
  sortColumn?: string;
  sortOrder?: string;
  onSort?: (sortKey: string, order: SortDirection | "") => void;
  tagOptions?: { id: string; label: string }[];
  customColumn?: CustomColumnDef;
  /** Full question text for custom study columns */
  infoTooltip?: string;
  /** @deprecated use filterActive */
  active?: boolean;
  /** @deprecated use filterConfig */
  config?: ColumnFilterConfig;
  /** @deprecated use filterValue */
  value?: ColumnFilterValue;
  /** @deprecated use onFilterApply */
  onApply?: (value: ColumnFilterValue) => void;
  /** @deprecated use onFilterClear */
  onClear?: () => void;
};

function getEmptyValue(type: ColumnFilterType): ColumnFilterValue {
  switch (type) {
    case "multiSelect":
    case "tagsAutocomplete":
      return [];
    case "dateRange":
      return { from: "", to: "" };
    case "customChoice":
      return [];
    default:
      return "";
  }
}

function renderSortIcon(sorted: boolean, sortOrder: string) {
  if (!sorted) {
    return <UnfoldMoreIcon fontSize="small" color="disabled" />;
  }
  if (sortOrder === "ASC") {
    return <ArrowUpwardIcon fontSize="small" color="primary" />;
  }
  return <ArrowDownwardIcon fontSize="small" color="primary" />;
}

const ColumnHeaderFilter = ({
  label,
  filterActive: filterActiveProp,
  filterConfig: filterConfigProp,
  filterValue: filterValueProp,
  onFilterApply,
  onFilterClear,
  sortConfig,
  sortColumn = "",
  sortOrder = "",
  onSort,
  tagOptions = [],
  customColumn,
  infoTooltip,
  active,
  config,
  value,
  onApply,
  onClear,
}: Props) => {
  const filterConfig = filterConfigProp ?? config;
  const filterValue = filterValueProp ?? value ?? "";
  const filterActive = filterActiveProp ?? active ?? false;
  const handleFilterApply = onFilterApply ?? onApply;
  const handleFilterClear = onFilterClear ?? onClear;

  const hasFilter = !!filterConfig && !!handleFilterApply && !!handleFilterClear;
  const hasSort = !!sortConfig && !!onSort;
  const hasInfo = !!infoTooltip?.trim();

  const renderInfoIcon = () => {
    if (!hasInfo) return null;
    return (
      <Tooltip title={infoTooltip} arrow placement="top">
        <IconButton
          size="small"
          sx={{ cursor: "default", p: 0.25 }}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Question info for ${label}`}
        >
          <InfoOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  };

  const sorted = sortConfig
    ? isColumnSorted(sortConfig.sortKey, sortColumn, sortOrder)
    : false;

  const sortLabels = sortConfig
    ? getSortOptionLabels(sortConfig.sortType)
    : null;

  const sortTooltip = sorted
    ? sortOrder === "ASC"
      ? sortLabels?.asc
      : sortLabels?.desc
    : "Click to sort";

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<ColumnFilterValue>(filterValue);
  const [dateError, setDateError] = useState("");

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (open) {
      setDraft(filterValue);
      setDateError("");
    }
  }, [open, filterValue]);

  const handleFilterOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    if (!filterConfig || !handleFilterApply) return;
    if (filterConfig.type === "dateRange") {
      const range = draft as DateRangeValue;
      if (range.from && range.to && range.from > range.to) {
        setDateError("From date must be on or before To date");
        return;
      }
    }
    if (filterConfig?.type === "customNumber") {
      const customValue =
        typeof draft === "object" && draft !== null && "value" in draft
          ? ((draft as { key: string; value: string | string[] }).value as string)
          : "";
      const decoded = decodeNumberFilter(customValue);
      if (decoded.op === "between") {
        const err = getBetweenError(decoded.min || "", decoded.max || "");
        if (err) {
          setDateError(err);
          return;
        }
      }
    }
    handleFilterApply(draft);
    handleClose();
  };

  const handleClearFilter = () => {
    if (!filterConfig || !handleFilterClear) return;
    setDraft(getEmptyValue(filterConfig.type));
    handleFilterClear();
    handleClose();
  };

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (!sortConfig || !onSort) return;

    if (!sorted) {
      onSort(sortConfig.sortKey, "ASC");
      return;
    }
    if (sortOrder === "ASC") {
      onSort(sortConfig.sortKey, "DESC");
      return;
    }
    onSort("", "");
  };

  const renderFilterField = () => {
    if (!filterConfig) return null;
    switch (filterConfig.type) {
      case "yesNo":
        return (
          <YesNoFilterField
            value={draft as string}
            onChange={(v) => setDraft(v)}
            showLabel={false}
          />
        );
      case "multiSelect":
        return (
          <MultiSelectFilterField
            value={draft as string[]}
            onChange={(v) => setDraft(v)}
            options={filterConfig.options ?? []}
            showLabel={false}
          />
        );
      case "singleSelect":
        return (
          <SingleSelectFilterField
            value={draft as string}
            onChange={(v) => setDraft(v)}
            options={filterConfig.options ?? []}
            showLabel={false}
          />
        );
      case "text":
        return (
          <TextFilterField
            value={draft as string}
            onChange={(v) => setDraft(v)}
            showLabel={false}
            placeholder={`Filter ${label.toLowerCase()}`}
          />
        );
      case "dateRange":
        return (
          <>
            <DateRangeFilterField
              value={draft as DateRangeValue}
              onChange={(v) => {
                setDraft(v);
                setDateError("");
              }}
              showLabels
            />
            {dateError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {dateError}
              </Typography>
            )}
          </>
        );
      case "tagsAutocomplete":
        return (
          <TagsAutocompleteFilterField
            value={draft as string[]}
            onChange={(v) => setDraft(v)}
            options={tagOptions}
            showLabel={false}
          />
        );
      case "customText":
      case "customNumber":
      case "customDate":
      case "customChoice":
        if (!customColumn) return null;
        return (
          <CustomColumnFilterField
            column={customColumn}
            value={
              typeof draft === "object" && draft !== null && "value" in draft
                ? (draft as { value: string | string[] }).value
                : (draft as string | string[])
            }
            onChange={(v) => {
              setDraft({ key: customColumn.varname, value: v });
              setDateError("");
            }}
            showLabel={false}
          />
        );
      default:
        return null;
    }
  };

  if (!hasFilter && !hasSort) {
    if (!hasInfo) {
      return (
        <Typography component="span" variant="body2" fontWeight="bold">
          {label}
        </Typography>
      );
    }
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.25,
          maxWidth: "100%",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography component="span" variant="body2" fontWeight="bold">
          {label}
        </Typography>
        {renderInfoIcon()}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.25,
        maxWidth: "100%",
        whiteSpace: "nowrap",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Typography
        component="span"
        variant="body2"
        fontWeight={filterActive || sorted ? 700 : "bold"}
        sx={{
          whiteSpace: "nowrap",
          flexShrink: 0,
          color: filterActive || sorted ? "primary.main" : "inherit",
        }}
      >
        {label}
      </Typography>

      {renderInfoIcon()}

      {hasSort && (
        <Tooltip title={sortTooltip ?? "Sort"} arrow>
          <IconButton
            size="small"
            onClick={handleSortClick}
            aria-label={`Sort ${label}`}
            sx={{ p: 0.25 }}
          >
            {renderSortIcon(sorted, sortOrder)}
          </IconButton>
        </Tooltip>
      )}

      {hasFilter && (
        <>
          <IconButton
            size="small"
            onClick={handleFilterOpen}
            aria-label={`Filter ${label}`}
            sx={{ p: 0.25 }}
          >
            <Badge color="primary" variant="dot" invisible={!filterActive}>
              <FilterListIcon
                fontSize="small"
                color={filterActive ? "primary" : "disabled"}
              />
            </Badge>
          </IconButton>

          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ p: 2, width: 300 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                Filter
              </Typography>
              {renderFilterField()}
              {filterConfig?.type === "customNumber" && dateError && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                  {dateError}
                </Typography>
              )}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  mt: 2,
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  size="small"
                  onClick={handleClearFilter}
                  variant="outlined"
                >
                  Clear
                </Button>
                <Button size="small" onClick={handleApply} variant="contained">
                  Apply
                </Button>
              </Box>
            </Box>
          </Popover>
        </>
      )}
    </Box>
  );
};

export default ColumnHeaderFilter;
